// The blind count form's lines, and the count in progress kept on this phone: a dropped
// connection, an expired session or a closed tab does not lose what was counted (Sayım).
import type { CountSheetLine } from '@wms/domain';
import { store } from './session';

export interface FormLine {
  readonly key: string;
  readonly line: CountSheetLine;
  readonly qty: number | null;
  /** found in the bin although the sheet did not name it */
  readonly found: boolean;
  readonly lotNo: string;
  readonly expiryDate: string;
}

export const keyOf = (productId: string, lotNo: string | null, expiryDate: string | null) => `${productId}|${lotNo ?? ''}|${expiryDate ?? ''}`;

export const fromSheet = (line: CountSheetLine): FormLine => ({
  key: keyOf(line.productId, line.lotNo, line.expiryDate),
  line,
  qty: null,
  found: false,
  lotNo: line.lotNo ?? '',
  expiryDate: line.expiryDate ?? '',
});

/** Something was counted: a quantity typed or scanned, or a product added. */
export const hasCounts = (lines: readonly FormLine[]) => lines.some((l) => l.qty !== null || l.found);

export interface CountDraft {
  /** a recount starts blind again: a draft of another round is not restored */
  readonly round: number;
  readonly lines: readonly FormLine[];
  /** when it was last saved (ms) */
  readonly at: number;
}

const DRAFTS_KEY = 'wms.countDrafts';
/** a count left longer than this is not resumed */
const DRAFT_MAX_AGE_MS = 7 * 86_400_000;

type Drafts = Record<string, CountDraft>;
// per person and count task: on a shared phone, someone else's half count is not theirs to finish
const draftKey = (userId: string, taskId: string) => `${userId}|${taskId}`;

// what another version of the app saved may not fit this form: such a draft is left out (it would break the form each time the bin opens)
const isLine = (l: Partial<FormLine> | null) =>
  typeof l?.key === 'string' &&
  typeof l.found === 'boolean' &&
  typeof l.lotNo === 'string' &&
  typeof l.expiryDate === 'string' &&
  (l.qty === null || (typeof l.qty === 'number' && Number.isInteger(l.qty) && l.qty >= 0)) &&
  typeof l.line?.productId === 'string' &&
  typeof l.line.name === 'string' &&
  typeof l.line.sku === 'string' &&
  Array.isArray(l.line.barcodes);

function readAll(now: number): Drafts {
  try {
    const saved: unknown = JSON.parse(store.get(DRAFTS_KEY) ?? '{}');
    if (!saved || typeof saved !== 'object') return {};
    const fresh: Drafts = {};
    for (const [key, draft] of Object.entries(saved as Record<string, Partial<CountDraft>>)) {
      if (typeof draft?.at === 'number' && now - draft.at < DRAFT_MAX_AGE_MS && typeof draft.round === 'number' && Array.isArray(draft.lines) && draft.lines.every(isLine)) {
        fresh[key] = draft as CountDraft;
      }
    }
    return fresh;
  } catch {
    return {};
  }
}

const writeAll = (drafts: Drafts) => store.set(DRAFTS_KEY, Object.keys(drafts).length > 0 ? JSON.stringify(drafts) : null);

/** The count this person left half done on this phone, for the task's current round. */
export function readDraft(userId: string, task: { readonly id: string; readonly round: number }, now = Date.now()): CountDraft | null {
  const draft = readAll(now)[draftKey(userId, task.id)];
  return draft && draft.round === task.round && hasCounts(draft.lines) ? draft : null;
}

/** Keeps the form as it is now; a form with nothing counted leaves nothing behind. */
export function writeDraft(userId: string, task: { readonly id: string; readonly round: number }, lines: readonly FormLine[], now = Date.now()): void {
  const drafts = readAll(now);
  const key = draftKey(userId, task.id);
  if (hasCounts(lines)) drafts[key] = { round: task.round, lines, at: now };
  else delete drafts[key];
  writeAll(drafts);
}

/** The count was sent, or its counts were thrown away. */
export function dropDraft(userId: string, taskId: string, now = Date.now()): void {
  const drafts = readAll(now);
  const key = draftKey(userId, taskId);
  if (!(key in drafts)) return;
  delete drafts[key];
  writeAll(drafts);
}

/** Of these tasks, the ones this person left half counted on this phone (the list marks them); a recount's old round is not. */
export function draftTaskIds(userId: string, tasks: readonly { readonly id: string; readonly round: number }[], now = Date.now()): ReadonlySet<string> {
  const drafts = readAll(now);
  return new Set(
    tasks
      .filter((t) => {
        const draft = drafts[draftKey(userId, t.id)];
        return draft !== undefined && draft.round === t.round && hasCounts(draft.lines);
      })
      .map((t) => t.id),
  );
}

/**
 * The fresh sheet with the saved counts in it. Products added in the bin come back; a counted line
 * the sheet no longer names (moved out meanwhile) stays, so nothing counted is lost.
 */
export function restoreLines(fresh: readonly FormLine[], saved: readonly FormLine[]): FormLine[] {
  const byKey = new Map(saved.map((l) => [l.key, l]));
  const merged = fresh.map((l) => {
    const kept = byKey.get(l.key);
    return kept && !kept.found ? { ...l, qty: kept.qty } : l;
  });
  const named = new Set(fresh.map((l) => l.key));
  return [...merged, ...saved.filter((l) => !named.has(l.key) && (l.found || l.qty !== null))];
}
